import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Download, Trophy, Calendar } from 'lucide-react'
import { LeaderboardEntry } from '@/types'
import html2canvas from 'html2canvas'
import { useRef, useState } from 'react'
import LeaderboardContainer from './LeaderboardContainer'
import { LeaderboardAvatarPreloader, LeaderboardLoadingIndicator } from './LeaderboardAvatarPreloader'

export default function TodayLeaderboard() {
  const leaderboardRef = useRef<HTMLDivElement>(null)
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [avatarLoadProgress, setAvatarLoadProgress] = useState(0)

  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ['leaderboard', 'today', selectedDate],
    queryFn: () => api.getLeaderboard('today', selectedDate),
    refetchInterval: selectedDate === new Date().toISOString().split('T')[0] ? 30000 : undefined, // 只有今天才自动刷新
    staleTime: 1000 * 60 * 2, // 2分钟内不重新获取
    cacheTime: 1000 * 60 * 10, // 缓存10分钟
  })

  const handleDownload = async () => {
    if (!leaderboardRef.current) return

    try {
      // 添加html2canvas专用类
      leaderboardRef.current.classList.add('html2canvas')

      const canvas = await html2canvas(leaderboardRef.current, {
        useCORS: true,
        allowTaint: true,
        background: '#ffffff',
        logging: false // 关闭日志
      })

      // 移除html2canvas专用类
      leaderboardRef.current.classList.remove('html2canvas')

      const link = document.createElement('a')
      link.download = `日榜-${selectedDate}.png`
      link.href = canvas.toDataURL('image/png', 1.0)
      link.click()
    } catch (error) {
      console.error('下载失败:', error)
      // 确保移除类，即使出错也要清理
      if (leaderboardRef.current) {
        leaderboardRef.current.classList.remove('html2canvas')
      }
      alert('下载失败，请稍后重试')
    }
  }



  if (isLoading) {
    return (
      <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-4">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">加载今日排行榜...</p>
        </div>
      </div>
    )
  }

  if (!leaderboard?.data || leaderboard.data.length === 0) {
    return (
      <div>
        {/* 日期选择器 */}
        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-4 mb-4">
          <div className="flex items-center justify-center space-x-3">
            <Calendar className="h-4 w-4 text-blue-600" />
            <label className="text-sm font-medium text-gray-700">选择日期:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-4">
          <div className="text-center py-8">
            <Trophy className="mx-auto h-10 w-10 text-gray-400 mb-3" />
            <p className="text-gray-500 text-sm">
              {selectedDate === new Date().toISOString().split('T')[0]
                ? `今日暂无阅读记录 ${new Date().toLocaleDateString('zh-CN').replace(/\//g, '/')}`
                : `${new Date(selectedDate).toLocaleDateString('zh-CN').replace(/\//g, '/')} 暂无阅读记录`
              }
            </p>
          </div>
        </div>
      </div>
    )
  }

  // 过滤出今日有阅读记录的用户
  const todayEntries = leaderboard.data.filter((entry: LeaderboardEntry) => entry.todayCount > 0)

  return (
    <div>
      {/* 头像预加载器 */}
      {todayEntries.length > 0 && (
        <LeaderboardAvatarPreloader
          entries={todayEntries}
          onProgress={setAvatarLoadProgress}
        />
      )}

      {/* 加载进度指示器 */}
      <LeaderboardLoadingIndicator
        progress={avatarLoadProgress}
        isVisible={todayEntries.length > 0 && avatarLoadProgress < 1}
      />

      {/* 日期选择器 */}
      <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-4 mb-4">
        <div className="flex items-center justify-center space-x-3">
          <Calendar className="h-4 w-4 text-blue-600" />
          <label className="text-sm font-medium text-gray-700">选择日期:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <LeaderboardContainer
        ref={leaderboardRef}
        title={selectedDate === new Date().toISOString().split('T')[0] ? '今日排行榜' : '日榜排行榜'}
        subtitle={selectedDate === new Date().toISOString().split('T')[0]
          ? `记录今日阅读成果 ${new Date().toLocaleDateString('zh-CN').replace(/\//g, '/')}`
          : `记录阅读成果 ${new Date(selectedDate).toLocaleDateString('zh-CN').replace(/\//g, '/')}`
        }
        entries={todayEntries}
        type="today"
        emptyMessage={selectedDate === new Date().toISOString().split('T')[0]
          ? `今日暂无阅读记录 ${new Date().toLocaleDateString('zh-CN').replace(/\//g, '/')}`
          : `${new Date(selectedDate).toLocaleDateString('zh-CN').replace(/\//g, '/')} 暂无阅读记录`
        }
      />

      {/* 今日统计 */}
      {todayEntries.length > 0 && (
        <div className="p-4 bg-gray-50/50 border-t border-gray-100/50">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-lg font-semibold text-blue-600">
                {todayEntries.length}
              </div>
              <div className="text-xs text-gray-500">今日参与</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-green-600">
                {todayEntries.reduce((sum, entry) => sum + entry.todayCount, 0)}
              </div>
              <div className="text-xs text-gray-500">今日总遍数</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-purple-600">
                {Math.round(todayEntries.reduce((sum, entry) => sum + entry.todayCount, 0) / todayEntries.length * 10) / 10}
              </div>
              <div className="text-xs text-gray-500">今日平均</div>
            </div>
          </div>
        </div>
      )}

      {/* 下载按钮 */}
      <div className="flex justify-center mt-4">
        <button
          onClick={handleDownload}
          className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-all shadow-lg"
        >
          <Download className="h-4 w-4" />
          <span>下载图片</span>
        </button>
      </div>
    </div>
  )
}
